import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // ⚠️ สลับระหว่าง localhost และ production
  // 🔹 สำหรับ localhost: uncomment บรรทัดที่ 1, comment บรรทัดที่ 2
  // 🔹 สำหรับ production: comment บรรทัดที่ 1, uncomment บรรทัดที่ 2
  // const API_BASE_URL = 'http://localhost:8000';                                    // [1] localhost
   const API_BASE_URL = 'https://mfuwellnesscenter-server.dietz.asia';           // [2] production

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('mfu_token');
      const storedUser = localStorage.getItem('mfu_user');

      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Login with username and password
  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        username,
        password
      });

      if (response.data.status === 'success') {
        const { user: userData, token: tokenData } = response.data.data;

        // Save to state
        setUser(userData);
        setToken(tokenData);

        // Save to localStorage
        localStorage.setItem('mfu_token', tokenData);
        localStorage.setItem('mfu_user', JSON.stringify(userData));

        return { success: true, user: userData };
      } else {
        return { success: false, message: response.data.message || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'เกิดข้อผิดพลาดในการเข้าสู่ระบบ'
      };
    }
  };

  // Login with token (auto-login from URL)
  const loginWithToken = async (tokenString) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/check-token`, {
        token: tokenString
      });

      if (response.data.status === 'success') {
        const { user: userData, token: tokenData } = response.data.data;

        // Save to state
        setUser(userData);
        setToken(tokenData);

        // Save to localStorage
        localStorage.setItem('mfu_token', tokenData);
        localStorage.setItem('mfu_user', JSON.stringify(userData));

        return { success: true, user: userData };
      } else {
        return { success: false, message: response.data.message || 'Invalid token' };
      }
    } catch (error) {
      console.error('Token login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Token ไม่ถูกต้องหรือหมดอายุ'
      };
    }
  };

  // Verify token validity
  const verifyToken = async (tokenString) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/check-token`, {
        token: tokenString || token
      });

      if (response.data.status !== 'success') {
        throw new Error('Token is invalid');
      }

      return true;
    } catch (error) {
      throw error;
    }
  };

  // Logout
  const logout = async () => {
    try {
      if (user?.id) {
        await axios.post(`${API_BASE_URL}/api/auth/logout`, {
          user_id: user.id
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear state
      setUser(null);
      setToken(null);

      // Clear localStorage
      localStorage.removeItem('mfu_token');
      localStorage.removeItem('mfu_user');
    }
  };

  // Get user profile
  const getProfile = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/auth/profile`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.status === 'success') {
        const userData = response.data.data.user;
        setUser(userData);
        localStorage.setItem('mfu_user', JSON.stringify(userData));
        return userData;
      }
    } catch (error) {
      console.error('Get profile error:', error);
      return null;
    }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user && !!token,
    login,
    loginWithToken,
    logout,
    getProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
